import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslocoService } from '@ngneat/transloco';
import {
  MyProfileUser,
  ResigteringService,
} from '../../../../services/resigtering.service';

@Component({
  selector: 'app-myprofile',
  templateUrl: './myprofile.component.html',
  styleUrl: './myprofile.component.scss',
})
export class MyprofileComponent implements OnInit {
  user?: MyProfileUser;
  profileForm: FormGroup;

  isLoading = true;
  isEditing = false;
  isSaving = false;
  isUploadingCv = false;
  errorMessage = '';
  successMessage = '';

  selectedCvFile: File | null = null;
  cvDragOver = false;
  cvErrorKey = '';

  private readonly maxCvFileSize = 5 * 1024 * 1024;

  constructor(
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private myProfileService: ResigteringService,
    private transloco: TranslocoService
  ) {
    this.profileForm = this.fb.group({
      first_name: ['', [Validators.required, Validators.minLength(2)]],
      last_name: ['', [Validators.required, Validators.minLength(2)]],
      username: ['', [Validators.required, Validators.minLength(3)]],
      phone: ['', [Validators.maxLength(15)]],
    });
  }

  ngOnInit(): void {
    const routeUserId = this.route.snapshot.paramMap.get('id');

    if (routeUserId) {
      this.loadUser(routeUserId);
      return;
    }

    const savedUserId = this.getUserIdFromStorage();

    if (savedUserId) {
      this.loadUser(savedUserId);
      return;
    }

    this.myProfileService.getUserId().subscribe({
      next: (res) => {
        if (!res.user_id) {
          this.handleError('profile.errors.userIdNotFound');
          return;
        }

        this.loadUser(res.user_id);
      },
      error: () => {
        this.handleError('profile.errors.currentUserFailed');
      },
    });
  }

  loadUser(userId: number | string): void {
    this.isLoading = true;
    this.errorMessage = '';

    this.myProfileService.getUserById(userId).subscribe({
      next: (res) => {
        this.user = res;
        this.patchProfileForm();
        this.isLoading = false;
      },
      error: () => {
        this.handleError('profile.errors.loadFailed');
      },
    });
  }

  get fullName(): string {
    if (!this.user) return '';
    return `${this.user.first_name || ''} ${this.user.last_name || ''}`.trim();
  }

  get profileInitial(): string {
    const source =
      this.user?.first_name ||
      this.user?.username ||
      this.user?.last_name ||
      '';

    return source.trim().charAt(0).toUpperCase() || '?';
  }

get currentCvUrl(): string {
  const cvPath = this.user?.user_cv || this.user?.user_cv_url || '';

  return this.buildBackendFileUrl(cvPath);
}

private buildBackendFileUrl(path: string): string {
  if (!path) return '';

  const backendBaseUrl = 'https://backendtest.blueboxpet.com';

  // لو الرابط راجع كامل بالفعل
  if (path.startsWith('http://') || path.startsWith('https://')) {
    // لو فيه /public خلاص رجعه زي ما هو
    if (path.includes('/public/')) {
      return path;
    }

    // لو راجع كامل بس ناقص /public قبل uploads
    return path.replace('/uploads/', '/public/uploads/');
  }

  const cleanPath = path.replace(/^\/+/, '');

  // لو path راجع public/uploads/...
  if (cleanPath.startsWith('public/')) {
    return `${backendBaseUrl}/${cleanPath}`;
  }

  // لو path راجع uploads/users/cvs/...
  return `${backendBaseUrl}/public/${cleanPath}`;
}
get currentCvName(): string {
  const cv = this.currentCvUrl;

  if (!cv) {
    return this.t('profile.cv.cvFile');
  }

  const clean = cv.split('?')[0];
  const parts = clean.split('/');

  return decodeURIComponent(
    parts[parts.length - 1] || this.t('profile.cv.cvFile')
  );
}
  get currentCvType(): string {
    const name = this.currentCvName.toLowerCase();

    if (name.endsWith('.pdf')) return 'PDF';
    if (name.endsWith('.doc')) return 'DOC';
    if (name.endsWith('.docx')) return 'DOCX';

    return 'CV';
  }
  startEdit(): void {
    if (!this.user) return;

    this.patchProfileForm();
    this.selectedCvFile = null;
    this.cvDragOver = false;
    this.cvErrorKey = '';
    this.isEditing = true;
    this.errorMessage = '';
    this.successMessage = '';
  }

  cancelEdit(): void {
    this.patchProfileForm();
    this.selectedCvFile = null;
    this.cvDragOver = false;
    this.cvErrorKey = '';
    this.isEditing = false;
    this.isSaving = false;
    this.errorMessage = '';
  }

  isInvalid(controlName: string): boolean {
    const control = this.profileForm.get(controlName);
    return !!(control && control.touched && control.invalid);
  }

  onCvFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (file) {
      this.setCvFile(file);
    }

    input.value = '';
  }

  onCvDragOver(event: DragEvent): void {
    event.preventDefault();

    if (this.isSaving || this.isUploadingCv) return;

    this.cvDragOver = true;
  }

  onCvDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.cvDragOver = false;
  }

  onCvDrop(event: DragEvent): void {
    event.preventDefault();

    this.cvDragOver = false;

    if (this.isSaving || this.isUploadingCv) return;

    const file = event.dataTransfer?.files?.[0];

    if (file) {
      this.setCvFile(file);
    }
  }

  setCvFile(file: File): void {
    this.cvErrorKey = '';

    const fileName = file.name.toLowerCase();

    const isAllowed =
      file.type === 'application/pdf' ||
      file.type === 'application/msword' ||
      file.type ===
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
      fileName.endsWith('.pdf') ||
      fileName.endsWith('.doc') ||
      fileName.endsWith('.docx');

    if (!isAllowed) {
      this.selectedCvFile = null;
      this.cvErrorKey = 'profile.cv.invalidType';
      return;
    }

    if (file.size > this.maxCvFileSize) {
      this.selectedCvFile = null;
      this.cvErrorKey = 'profile.cv.maxSize';
      return;
    }

    this.selectedCvFile = file;
  }

  removeCvFile(event: MouseEvent): void {
    event.stopPropagation();

    this.selectedCvFile = null;
    this.cvDragOver = false;
    this.cvErrorKey = '';
  }

  uploadSelectedCv(event?: MouseEvent): void {
    event?.stopPropagation();

    if (!this.user || !this.selectedCvFile || this.isUploadingCv) {
      return;
    }

    this.isUploadingCv = true;
    this.errorMessage = '';
    this.successMessage = '';
    this.cvErrorKey = '';

    const currentUser = this.user;

    this.myProfileService
      .uploadUserCvWithoutToken(currentUser.id, this.selectedCvFile)
      .subscribe({
        next: (res) => {
          const updatedUser = res?.user || res?.data || res;

          this.user = {
            ...currentUser,
            ...updatedUser,
            user_cv:
              updatedUser?.user_cv ||
              updatedUser?.cv ||
              updatedUser?.path ||
              updatedUser?.file_path ||
              currentUser.user_cv,
            user_cv_url:
              updatedUser?.user_cv_url ||
              updatedUser?.cv_url ||
              updatedUser?.url ||
              updatedUser?.file_url ||
              currentUser.user_cv_url,
          };

          this.selectedCvFile = null;
          this.cvDragOver = false;
          this.isUploadingCv = false;
          this.successMessage = this.t('profile.cv.uploadSuccess');
          this.updateStoredUser();

          if (!this.currentCvUrl) {
            this.reloadCurrentUserSilently();
          }
        },
        error: (err) => {
          this.isUploadingCv = false;
          this.errorMessage =
            err?.error?.message || this.t('profile.cv.uploadFailed');
        },
      });
  }

  formatFileSize(bytes: number): string {
    if (!bytes) {
      return '0 KB';
    }

    const kb = bytes / 1024;

    if (kb < 1024) {
      return `${kb.toFixed(1)} KB`;
    }

    return `${(kb / 1024).toFixed(1)} MB`;
  }

  getFileTypeLabel(file: File | null): string {
    if (!file) {
      return 'CV';
    }

    const name = file.name.toLowerCase();

    if (name.endsWith('.pdf')) {
      return 'PDF';
    }

    if (name.endsWith('.doc')) {
      return 'DOC';
    }

    if (name.endsWith('.docx')) {
      return 'DOCX';
    }

    return 'CV';
  }

  saveProfile(): void {
    if (!this.user || this.profileForm.invalid || this.isSaving) {
      this.profileForm.markAllAsTouched();
      return;
    }

    this.isSaving = true;
    this.errorMessage = '';
    this.successMessage = '';

    this.myProfileService
      .editUser(this.user.id, this.buildProfileFormData())
      .subscribe({
        next: (res) => {
          const updatedUser = res?.user || res?.data || res;

          this.user = {
            ...this.user,
            ...updatedUser,
            first_name: this.profileForm.value.first_name,
            last_name: this.profileForm.value.last_name,
            username: this.profileForm.value.username,
            phone: this.profileForm.value.phone,
          };

          this.updateStoredUser();

          this.selectedCvFile = null;
          this.cvDragOver = false;
          this.cvErrorKey = '';

          this.isSaving = false;
          this.isEditing = false;
          this.successMessage = this.t('profile.successUpdated');
        },
        error: (err) => {
          this.isSaving = false;
          this.errorMessage =
            err?.error?.message || this.t('profile.errors.updateFailed');
        },
      });
  }

  private patchProfileForm(): void {
    if (!this.user) return;

    this.profileForm.patchValue({
      first_name: this.user.first_name || '',
      last_name: this.user.last_name || '',
      username: this.user.username || '',
      phone: this.user.phone || '',
    });
  }

  private buildProfileFormData(): FormData {
    const formValue = this.profileForm.value;
    const formData = new FormData();

    formData.append('first_name', String(formValue.first_name || '').trim());
    formData.append('last_name', String(formValue.last_name || '').trim());
    formData.append('username', String(formValue.username || '').trim());
    formData.append('phone', String(formValue.phone || '').trim());

    if (this.user?.type !== undefined && this.user?.type !== null) {
      formData.append('type', String(this.user.type));
    }

    if (this.user?.user_type) {
      formData.append('user_type', this.user.user_type);
    }

    return formData;
  }

  private reloadCurrentUserSilently(): void {
    if (!this.user?.id) return;

    this.myProfileService.getUserById(this.user.id).subscribe({
      next: (res) => {
        this.user = res;
        this.updateStoredUser();
      },
    });
  }

  private updateStoredUser(): void {
    if (!this.user) return;

    const storedUser = localStorage.getItem('user');
    const cv = this.user.user_cv || this.user.user_cv_url || '';

    try {
      const parsedUser = storedUser ? JSON.parse(storedUser) : {};

      localStorage.setItem(
        'user',
        JSON.stringify({
          ...parsedUser,
          id: this.user.id,
          first_name: this.user.first_name,
          last_name: this.user.last_name,
          username: this.user.username,
          phone: this.user.phone,
          user_cv: this.user.user_cv,
          user_cv_url: this.user.user_cv_url,
        })
      );

      if (cv) {
        localStorage.setItem('user_cv', cv);
        localStorage.setItem('user_cv_url', cv);
      } else {
        localStorage.removeItem('user_cv');
        localStorage.removeItem('user_cv_url');
      }
    } catch {
      localStorage.removeItem('user');
    }
  }

  private getUserIdFromStorage(): number | null {
    const storedUser = localStorage.getItem('user');

    if (!storedUser) return null;

    try {
      const user = JSON.parse(storedUser);
      return user?.id || null;
    } catch {
      return null;
    }
  }

  private handleError(message: string): void {
    this.errorMessage = this.t(message);
    this.isLoading = false;
  }

  private t(key: string): string {
    return this.transloco.translate(key);
  }
}
